import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Icon from '@mdi/react';
import {
  mdiTrayArrowUp,
  mdiEyeOutline,
  mdiDownload,
  mdiTrashCanOutline,
} from '@mdi/js';
import DisplayModal from '../DisplayModal/DisplayModal';

import './FileUploadDropzone.scss';

export default function FileUploadDropzone({
  onDrop,
  disabled,
  selectedFiles,
  className,
  handleDeleteFile,
  showDeleteButton = true,
  showFileList = true,
  showName = false,
  showDownloadButton = true,
  placeholder = 'Browse File/Drag and Drop File',
  ...props
}) {
  const [displayModalOpen, setDisplayModalOpen] = useState(false);
  const [displayModalFile, setDisplayModalFile] = useState(null);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: props?.multiple || false,
    disabled,
  });

  const inputDisplayText = files => {
    if (!files) {
      return placeholder;
    }
    if (!files || files.length === 0) {
      return placeholder;
    }
    if (files?.length === 1) {
      if (showName) {
        return files[0].name;
      }
      return '1 file selected';
    }
    return `${files.length} files selected`;
  };

  return (
    <>
      <div
        {...getRootProps({
          tabIndex: 0,
          role: 'button',
          'aria-disabled': !!disabled,
        })}
        className={`dropzone rounded p-3 ${isDragActive ? 'drag-active' : ''} ${disabled ? 'disabled' : ''} ${props?.error ? 'error' : ''} ${className || ''}`}
        style={{
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
        onKeyDown={e => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
            e.preventDefault();
            e.currentTarget.querySelector('input[type="file"]')?.click();
          }
        }}
      >
        <input {...getInputProps()} title={props.tooltipTitle} />
        <div
          className="d-flex flex-row align-items-center justify-content-between"
          style={{
            minWidth: '100%',
          }}
        >
          <span
            className="text-muted input-file-upload-name"
            title={
              props.tooltipTitle?.trim() !== ''
                ? props.tooltipTitle
                : inputDisplayText(selectedFiles || [])
            }
          >
            {inputDisplayText(selectedFiles || [])}
          </span>
          <Icon path={mdiTrayArrowUp} size={0.7} color="#667085" />
        </div>
      </div>
      {/* Uploaded File Display */}
      {selectedFiles &&
        showFileList &&
        selectedFiles.map(file => (
          <div className="mt-2 px-3 py-1 file-display">
            <div className="d-flex align-items-center gap-2">
              <div>
                <div className="fw-semibold file-upload-name">{file.name}</div>
                <div className="text-muted" style={{ fontSize: '12px' }}>
                  {(file.size / 1024).toFixed(1)} KB
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center gap-2">
              <Icon
                path={mdiEyeOutline}
                size={0.7}
                color="#017EBD"
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setDisplayModalFile(file);
                  setDisplayModalOpen(true);
                }}
              />
              {showDownloadButton && (
                <Icon
                  path={mdiDownload}
                  size={0.7}
                  color="#667085"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    const url = URL.createObjectURL(file);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = file.name;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  }}
                />
              )}
              {showDeleteButton && (
                <Icon
                  path={mdiTrashCanOutline}
                  size={0.7}
                  color="#dc3545"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    if (handleDeleteFile) {
                      handleDeleteFile(file);
                    }
                  }}
                />
              )}
            </div>
          </div>
        ))}
      <DisplayModal
        isModalOpen={displayModalOpen}
        toggle={() => setDisplayModalOpen(false)}
        title="View Attachments"
        file={displayModalFile}
      />
    </>
  );
}
